import * as React from 'react';
import { OptionsEditor } from 'owa-options-core';
import { OptionsCheckbox } from 'owa-options-controls';
import { Toggle } from 'office-ui-fabric-react/lib/components/Toggle';
import getStore from '../data/store/store';
import showPeriodicEnabledOptions from '../data/mutators/showPeriodicEnabledOptions';
import showAutomaticReplyEnabledOptions from '../data/mutators/showAutomaticReplyEnabledOptions';
import setBlockCalendarEnabledState from '../data/mutators/setBlockCalendarEnabledState';
import setDeclineNewInvitationsEnabledState from '../data/mutators/setDeclineNewInvitationsEnabledState';
import setSendRepliesToContactsOnlyEnabledState from '../data/mutators/setSendRepliesToContactsOnlyEnabledState';
import setSendRepliesOutsideOrgEnabledState from '../data/mutators/setSendRepliesOutsideOrgEnabledState';
import intializeEditorViewState from '../data/mutators/intializeEditorViewStateMessage';
import intializeEditorViewOutsideOrgState from '../data/mutators/intializeEditorViewOutsideOrgState';
import declineMeetingsSelectedCategory from '../data/mutators/declineMeetingsSelectedCategory';
import setBlockCalendarTitle from '../data/mutators/setBlockCalendarTitle';
import updateCalendarEventSelections from '../data/mutators/updateCalendarEventSelections';
import setDeclineExistingCalendarEvents from '../actions/setDeclineExistingCalendarEvents';
import setEndDate from '../actions/setEndDate';
import setStartDate from '../actions/setStartDate';
import setStartTime from '../actions/setStartTime';
import setEndTime from '../actions/setEndTime';
import { getFallbackValueIfNull } from 'owa-options-core';
import strings from 'owa-strings/owa-options-strings';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { Dropdown, IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import AutomaticRepliesDateRangeSelector from './AutomaticRepliesDateRangeSelector';
import { formatUserDate, formatUserTime, OwaDate } from 'owa-datetime';
import {
    DetailsList,
    Selection,
    IColumn,
    ConstrainMode,
} from 'office-ui-fabric-react/lib/DetailsList';
import { MarqueeSelection } from 'office-ui-fabric-react/lib/MarqueeSelection';
import { observer } from 'mobx-react';
import DeclineMeetingSelectionState from '../data/store/schema/DeclineMeetingSelectionState';
import { OptionDescription } from 'owa-options-controls';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import {
    getIsAutomaticReplyEnabled,
    getPeriodicReplyEnabled,
    getBlockCalendarEnabled,
    getDeclineNewInvitationsEnabled,
    getSendRepliesToContactsOnlyEnabled,
    getSendRepliesOutsideOrgEnabled,
    getBlockCalendarTitle,
    getDeclineMeetingSelectedCategoryKey,
} from '../selectors/getStoreProperties';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { CalendarEvent } from '../data/store/schema/AutomaticRepliesOptionState';

const styles = require('./AutomaticRepliesFull.scss');

const listMinWidth = 80;
const listMaxWidth = 80;
const subMinWidth = 150;
const subMaxWidth = 150;

@observer
export default class AutomaticRepliesFull extends React.Component<{}, {}> {
    private calendarEventSelection: Selection;

    constructor(props: any) {
        super(props);
        this.calendarEventSelection = new Selection({
            onSelectionChanged: this.onCalendarEventSelected.bind(this),
        });
        this.onStartDateSelection = this.onStartDateSelection.bind(this);
        this.onEndDateSelection = this.onEndDateSelection.bind(this);
        this.onStartTimeChanged = this.onStartTimeChanged.bind(this);
        this.onEndTimeChanged = this.onEndTimeChanged.bind(this);
        this.toggleDeclineExistingInvitations = this.toggleDeclineExistingInvitations.bind(this);
    }

    render() {
        let optionStore = getStore().currentState;
        let isEnterpriseUser = !isConsumer();
        let isAutomaticReplyEnabled = getIsAutomaticReplyEnabled();
        let periodicReplyEnabled = getPeriodicReplyEnabled();
        let blockCalendarEnabled = getBlockCalendarEnabled();
        let declineNewInvitationsEnabled = getDeclineNewInvitationsEnabled();
        let sendOnlyToContacts = getSendRepliesToContactsOnlyEnabled();
        let sendRepliesOutsideOrg = getSendRepliesOutsideOrgEnabled();
        let blockCalendarInputTitle = getBlockCalendarTitle();
        let declineMeetingMessageCategories = [
            {
                key: DeclineMeetingSelectionState.UseAutomaticReplyMessage,
                text: strings.options_automaticReplies_emailReplies_messageSelect_myMessage,
            },
            {
                key: DeclineMeetingSelectionState.CreateNewMessage,
                text: strings.options_automaticReplies_emailReplies_messageSelect_createMessage,
            },
        ];
        let declineMeetingSelectedCategoryKey = getDeclineMeetingSelectedCategoryKey();
        let declineExistingCalendarEventsEnabled = getFallbackValueIfNull(
            optionStore.declineExistingCalendarEventsEnabled,
            false // not considering the value from server.
        );
        let declineMeetingCustomMessageEnabled: boolean = getFallbackValueIfNull(
            declineMeetingSelectedCategoryKey == DeclineMeetingSelectionState.CreateNewMessage,
            false
        );

        return (
            <div>
                <OptionDescription>
                    {strings.options_automaticReplies_emailReplies}
                </OptionDescription>
                <div className={styles.showAutoReplyOptions}>
                    <Toggle
                        checked={isAutomaticReplyEnabled}
                        onText={
                            strings.options_automaticReplies_emailReplies_turnOffAutomaticReplies
                        }
                        offText={
                            strings.options_automaticReplies_emailReplies_turnOnAutomaticReplies
                        }
                        className={styles.toggle}
                        onChanged={this.toggleAutomaticReply}
                    />
                </div>
                <OptionsCheckbox
                    checked={periodicReplyEnabled}
                    label={strings.options_automaticReplies_emailReplies_sendInTimePeriod}
                    onChange={this.togglePeriodicReply}
                    disabled={!isAutomaticReplyEnabled}
                />
                <div className={styles.showAutoReplyOptions}>
                    {
                        <AutomaticRepliesDateRangeSelector
                            isEnabled={isAutomaticReplyEnabled && periodicReplyEnabled}
                            onStartDateSelection={this.onStartDateSelection}
                            onEndDateSelection={this.onEndDateSelection}
                            onStartTimeChanged={this.onStartTimeChanged}
                            onEndTimeChanged={this.onEndTimeChanged}
                        />
                    }
                    <div className={styles.showPeriodicReplyOptions}>
                        {isAutomaticReplyEnabled &&
                            periodicReplyEnabled && [
                                <OptionsCheckbox
                                    key="block_calendar_checkbox"
                                    checked={blockCalendarEnabled}
                                    label={
                                        strings.options_automaticReplies_emailReplies_blockCalendar
                                    }
                                    onChange={this.toggleBlockCalendar}
                                />,
                                <div key="block_calendar_suboptions">
                                    {blockCalendarEnabled && (
                                        <div>
                                            <TextField
                                                className={styles.blockCalendarInput}
                                                value={blockCalendarInputTitle}
                                                onChanged={this.onBlockCalendarTitleChanged}
                                                disabled={!blockCalendarEnabled}
                                                placeholder={
                                                    strings.options_automaticReplies_emailReplies_blockTitle
                                                }
                                            />
                                        </div>
                                    )}
                                </div>,
                                <OptionsCheckbox
                                    key="decline_new_invitations"
                                    checked={declineNewInvitationsEnabled}
                                    label={
                                        strings.options_automaticReplies_emailReplies_automaticDecline
                                    }
                                    onChange={this.toggleDeclineNewInvitations}
                                />,
                                <OptionsCheckbox
                                    key="decline_existing_invitations"
                                    checked={declineExistingCalendarEventsEnabled}
                                    label={
                                        strings.options_automaticReplies_emailReplies_declineAndCancel
                                    }
                                    onChange={this.toggleDeclineExistingInvitations}
                                />,
                                <div key="calendar_events_list">
                                    {optionStore.calendarViewItems &&
                                        optionStore.calendarViewItems.length > 0 &&
                                        declineExistingCalendarEventsEnabled && (
                                            <div className={styles.declineExistingMeetings}>
                                                <Label>
                                                    {
                                                        strings.options_automaticReplies_emailReplies_cancel_meetings_select_message
                                                    }
                                                </Label>
                                                <Dropdown
                                                    selectedKey={declineMeetingSelectedCategoryKey}
                                                    options={declineMeetingMessageCategories}
                                                    className={styles.dropDown}
                                                    onChanged={
                                                        this.onDeclineMeetingSeletedCategoryChanged
                                                    }
                                                />
                                                <div>
                                                    {declineMeetingCustomMessageEnabled &&
                                                        isAutomaticReplyEnabled && (
                                                            <OptionsEditor
                                                                editorViewState={
                                                                    optionStore.editorViewStateDeclineMeeting
                                                                }
                                                                ribbonViewState={optionStore.ribbon}
                                                                initialize={
                                                                    intializeEditorViewState
                                                                }
                                                                watermark={
                                                                    strings.options_automaticReplies_emailReplies_optionsEditor_watermark
                                                                }
                                                            />
                                                        )}
                                                </div>
                                                <Label>
                                                    {
                                                        strings.options_automaticReplies_emailReplies_selectClearEvents
                                                    }
                                                </Label>
                                                <MarqueeSelection
                                                    selection={this.calendarEventSelection}>
                                                    <DetailsList
                                                        items={optionStore.calendarViewItems}
                                                        columns={this.buildCalendarEventsColumns()}
                                                        selection={this.calendarEventSelection}
                                                        onRenderItemColumn={this.onRenderItemColumn}
                                                        constrainMode={
                                                            ConstrainMode.horizontalConstrained
                                                        }
                                                        className={
                                                            styles.rootIsHorizontalConstrained
                                                        }
                                                    />
                                                </MarqueeSelection>
                                            </div>
                                        )}
                                </div>,
                            ]}
                    </div>
                </div>
                <div>
                    {isAutomaticReplyEnabled && isEnterpriseUser && (
                        <div className={styles.sendAutomaticRepliesInOrgText}>
                            <Label>
                                {
                                    strings.options_automaticReplies_emailEnterpriseReplies_sendAutomaticInOrg
                                }
                            </Label>
                        </div>
                    )}
                </div>
                {isAutomaticReplyEnabled && (
                    <OptionsEditor
                        editorViewState={optionStore.editorViewStateMessage}
                        ribbonViewState={optionStore.ribbon}
                        initialize={intializeEditorViewState}
                        watermark={
                            strings.options_automaticReplies_emailReplies_optionsEditor_watermark
                        }
                    />
                )}
                <div className={styles.showAutoReplyOptions}>
                    {isEnterpriseUser && (
                        <OptionsCheckbox
                            checked={sendRepliesOutsideOrg}
                            label={
                                strings.options_automaticReplies_emailEnterpriseReplies_sendAutomaticOutOrg
                            }
                            onChange={this.toggleSendRepliesOutsideOrg}
                            disabled={!isAutomaticReplyEnabled}
                        />
                    )}
                    {
                        <OptionsCheckbox
                            checked={sendOnlyToContacts}
                            label={strings.options_automaticReplies_emailReplies_sendOnlyToContacts}
                            onChange={this.toggleSendRepliesOnlytoContacts}
                            className={isEnterpriseUser && styles.sendOnlyToContacts}
                            disabled={
                                !isAutomaticReplyEnabled ||
                                (isEnterpriseUser && !sendRepliesOutsideOrg)
                            }
                        />
                    }
                    {isEnterpriseUser && sendRepliesOutsideOrg && isAutomaticReplyEnabled && (
                        <OptionsEditor
                            editorViewState={optionStore.editorViewStateOutsideOrg}
                            ribbonViewState={optionStore.ribbon}
                            initialize={intializeEditorViewOutsideOrgState}
                            watermark={
                                strings.options_automaticReplies_emailReplies_optionsEditor_watermark
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    /**
     * Handler for toggling periodic replies status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private togglePeriodicReply(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        showPeriodicEnabledOptions(checked);
    }

    /**
     * Handler for toggling automatic replies status
     * @param {boolean} checked checked status
     */
    private toggleAutomaticReply(checked?: boolean) {
        showAutomaticReplyEnabledOptions(checked);
    }

    /**
     * Handler for toggling block calendar status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private toggleBlockCalendar(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setBlockCalendarEnabledState(checked);
    }

    /**
     * Handler for toggling decline new invitations status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private toggleDeclineNewInvitations(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setDeclineNewInvitationsEnabledState(checked);
    }

    /**
     * Handler for toggling send replies only to contact status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private toggleSendRepliesOnlytoContacts(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setSendRepliesToContactsOnlyEnabledState(checked);
    }

    /**
     * Handler for toggling send replies outside org status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private toggleSendRepliesOutsideOrg(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setSendRepliesOutsideOrgEnabledState(checked);
    }

    /**
     * Handler for toggling decline existing invitations status
     * @param {React.FormEvent<HTMLElement | HTMLInputElement>} ev form event
     * @param {boolean} checked checked status
     */
    private toggleDeclineExistingInvitations(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setDeclineExistingCalendarEvents(checked);
        this.calendarEventSelection.setAllSelected(false);
    }

    /**
     * Handler for changing block calendar title
     * @param {string} text new changed text
     */
    private onBlockCalendarTitleChanged(text: string) {
        setBlockCalendarTitle(text);
    }

    /**
     * Handler for changing start date
     * @param {OwaDate} date new start date
     */
    private onStartDateSelection(date: OwaDate) {
        setStartDate(date);
        this.calendarEventSelection.setAllSelected(false);
    }

    /**
     * Handler for changing end date
     * @param {OwaDate} date new end date
     */
    private onEndDateSelection(date: OwaDate) {
        setEndDate(date);
        this.calendarEventSelection.setAllSelected(false);
    }

    /**
     * Utility method to build the calendar event columns
     * @param {CalendarEvent[]} items calendar events
     */
    private buildCalendarEventsColumns() {
        let columns: IColumn[] = [];
        let displayFields = [
            strings.options_automaticReplies_emailReplies_cancel_meetings_subject_header,
            strings.options_automaticReplies_emailReplies_cancel_meetings_organiser_header,
            strings.options_automaticReplies_emailReplies_cancel_meetings_startTime_header,
            strings.options_automaticReplies_emailReplies_cancel_meetings_endTime_header,
        ];
        let displayKeys: (keyof CalendarEvent)[] = ['Subject', 'Organizer', 'Start', 'End'];
        displayKeys.map((k, i) => {
            columns[i] = {
                key: displayKeys[i],
                fieldName: null,
                name: displayFields[i],
                minWidth: displayKeys[i] === 'Subject' ? subMinWidth : listMinWidth,
                maxWidth: displayKeys[i] === 'Subject' ? subMaxWidth : listMaxWidth,
                isResizable: true,
            };
        });
        return columns;
    }

    /**
     * Handler invoked when calendar event is selected
     */
    private onCalendarEventSelected() {
        let selections: any[] = this.calendarEventSelection.getSelection();
        let selectionIds = selections.map((item: CalendarEvent) => {
            return item.ItemId.Id;
        });
        updateCalendarEventSelections(selectionIds);
    }

    /**
     * Handler when start time is changed
     * @param {OwaDate} date new start date
     */
    private onStartTimeChanged(date: OwaDate) {
        setStartTime(date);
        this.calendarEventSelection.setAllSelected(false);
    }

    /**
     * Handler when end time is changed
     * @param {OwaDate} date new end date
     */
    private onEndTimeChanged(date: OwaDate) {
        setEndTime(date);
        this.calendarEventSelection.setAllSelected(false);
    }

    /**
     * Handler when decline meeting message category is changed
     * @param {IDropdownOption} option new selected option
     */
    private onDeclineMeetingSeletedCategoryChanged(option: IDropdownOption) {
        declineMeetingsSelectedCategory(option.key as DeclineMeetingSelectionState);
    }

    /**
     * Handler for rendering column
     * @param item to render in details list
     * @param {number} index of item
     * @param {IColumn} column in list
     */
    private onRenderItemColumn(item: CalendarEvent, index: number, column: IColumn) {
        if (column.key === 'Organizer') {
            let organizer: SingleRecipientType = item[column.key];
            if (organizer && organizer.Mailbox) {
                return organizer.Mailbox.Name;
            }
        }
        if (column.key === 'Start' || column.key === 'End') {
            const date = item[column.key];
            return formatUserDate(date) + ' ' + formatUserTime(date);
        }
        return item[column.key];
    }
}
